import React from 'react';
import { string, element, bool, oneOf, shape } from 'prop-types';
import styled, { css } from 'styled-components';
import { scriptPropType } from '@bbc/gel-foundations/prop-types';
import { C_CONSENT_BACKGROUND, C_CONSENT_ACTION, C_CONSENT_CONTENT, C_WHITE } from '@bbc/psammead-styles/colours';
import { getGreatPrimer, getLongPrimer } from '@bbc/gel-foundations/typography';
import { GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_2_SCREEN_WIDTH_MIN, GEL_GROUP_5_SCREEN_WIDTH_MIN } from '@bbc/gel-foundations/breakpoints';
import { GEL_MARGIN_BELOW_400PX, GEL_MARGIN_ABOVE_400PX, GEL_SPACING_DBL, GEL_SPACING } from '@bbc/gel-foundations/spacings';
import { getSansRegular } from '@bbc/psammead-styles/font-styles';

var ltrRtl = function ltrRtl(ltrValue, rtlValue) {
  return css(["", ";"], function (_ref) {
    var dir = _ref.dir;
    return dir === 'ltr' ? ltrValue : rtlValue;
  });
};

var Wrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper",
  componentId: "sc-7g3fro-0"
})(["", " background-color:", ";padding:", " ", ";@media (min-width:", "){padding:", ";}"], function (_ref2) {
  var service = _ref2.service;
  return getSansRegular(service);
}, C_CONSENT_BACKGROUND, GEL_SPACING_DBL, GEL_MARGIN_BELOW_400PX, GEL_GROUP_2_SCREEN_WIDTH_MIN, GEL_MARGIN_ABOVE_400PX);
/*
 * The '&::after' below is to ensure that the background colour covers the
 * banner as the inner elements are float. The alernative is to have
 * another div inside. This implementation mirrors the current orbit banner.
 */

var CenterWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "CenterWrapper",
  componentId: "sc-7g3fro-1"
})(["max-width:", ";margin:0 auto;&::after{content:'\\0020';display:block;height:0;clear:both;overflow:hidden;visibility:hidden;}"], GEL_GROUP_5_SCREEN_WIDTH_MIN);
var Title = /*#__PURE__*/styled.h2.withConfig({
  displayName: "Title",
  componentId: "sc-7g3fro-2"
})(["", ";color:", ";font-weight:700;padding:0;margin:0;@media (min-width:", "){float:", ";", " width:22%;}"], function (_ref3) {
  var script = _ref3.script;
  return script && getGreatPrimer(script);
}, C_WHITE, GEL_GROUP_3_SCREEN_WIDTH_MIN, ltrRtl('left', 'right'), ltrRtl('margin-right: 3.5%;', 'margin-left: 3.5%;'));
/*
 * The '& li + li' below allows for styling every `li` element except the first.
 */

var Options = /*#__PURE__*/styled.ul.withConfig({
  displayName: "Options",
  componentId: "sc-7g3fro-3"
})(["", ";color:", ";font-weight:600;padding:0;margin:0;list-style-type:none;& li + li{padding-top:", ";}@media (min-width:", "){width:18%;float:", ";}"], function (_ref4) {
  var script = _ref4.script;
  return script && getLongPrimer(script);
}, C_CONSENT_ACTION, GEL_SPACING, GEL_GROUP_3_SCREEN_WIDTH_MIN, ltrRtl('right', 'left'));
var hoverFocusStyles = "\n  &:focus,\n  &:hover {\n    color: ".concat(C_WHITE, ";\n  }\n");
export var ConsentBannerText = /*#__PURE__*/styled.p.withConfig({
  displayName: "ConsentBannerText",
  componentId: "sc-7g3fro-4"
})(["", ";color:", ";@media (min-width:", "){margin:0;float:", ";width:53%;}a{color:", ";text-decoration:none;", "}"], function (_ref5) {
  var script = _ref5.script;
  return script && getLongPrimer(script);
}, C_CONSENT_CONTENT, GEL_GROUP_3_SCREEN_WIDTH_MIN, ltrRtl('left', 'right'), C_CONSENT_ACTION, hoverFocusStyles); // Style `button` and `a` as children due to inability to set `on`
// prop on styled component as required for the amp useage

var ListItem = /*#__PURE__*/styled.li.withConfig({
  displayName: "ListItem",
  componentId: "sc-7g3fro-5"
})(["& button{", ";color:", ";font-weight:700;background:none;border:none;padding:0;margin:0;cursor:pointer;", "}& a{color:", ";text-decoration:none;", "}"], function (_ref6) {
  var script = _ref6.script;
  return script && getGreatPrimer(script);
}, C_CONSENT_ACTION, hoverFocusStyles, C_CONSENT_ACTION, hoverFocusStyles);
export var ConsentBanner = function ConsentBanner(_ref7) {
  var dir = _ref7.dir,
      title = _ref7.title,
      text = _ref7.text,
      accept = _ref7.accept,
      reject = _ref7.reject,
      id = _ref7.id,
      hidden = _ref7.hidden,
      script = _ref7.script,
      service = _ref7.service;
  return /*#__PURE__*/React.createElement(Wrapper, {
    dir: dir,
    hidden: hidden,
    id: id,
    service: service
  }, /*#__PURE__*/React.createElement(CenterWrapper, {
    dir: dir
  }, /*#__PURE__*/React.createElement(Title, {
    dir: dir,
    script: script
  }, title), text, /*#__PURE__*/React.createElement(Options, {
    dir: dir,
    script: script
  }, /*#__PURE__*/React.createElement(ListItem, {
    dir: dir,
    script: script
  }, accept), /*#__PURE__*/React.createElement(ListItem, {
    dir: dir,
    script: script
  }, reject))));
};
ConsentBanner.propTypes = {
  dir: oneOf(['ltr', 'rtl']),
  title: string.isRequired,
  text: element.isRequired,
  accept: element.isRequired,
  reject: element.isRequired,
  id: string,
  hidden: bool,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired
};
ConsentBanner.defaultProps = {
  dir: 'ltr',
  id: null,
  hidden: null
};